import React from 'react';
import { connect } from 'react-redux';
import { Alert, Dropdown, MenuItem } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { assign, each, filter, find, isEmpty, size } from 'lodash';
import snakecaseKeys from 'snakecase-keys';
import {
  BrowserRouter as Router,
  Switch,
  Route
} from 'react-router-dom';
import DirtyShadowConfirmationPrompt from './dirty_shadow_confirmation_prompt';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  faAddressBook,
  faBolt,
  faBorderAll,
  faBriefcase,
  faBullhorn,
  faBullseye,
  faChartBar,
  faCheck,
  faCogs,
  faDollarSign,
  faEnvelope,
  faFileAlt,
  faFileInvoiceDollar,
  faFolder,
  faGlobe,
  faHandshake,
  faHome,
  faImage,
  faLink,
  faLock,
  faObjectGroup,
  faPaperclip,
  faRoad,
  faSatelliteDish,
  faSquarePollVertical,
  faTachometerAlt,
  faTags,
  faTh,
  faUniversity,
  faUsers
} from '@fortawesome/pro-solid-svg-icons';

import { clickAsLink } from 'helpers/link_helpers';
import confirm from 'components/confirm';
import UnstyledDropdownToggle from 'components/bootstrap_extensions/unstyled_dropdown_toggle';
import ProjectOverview from './project_overview';
import TaskList from 'components/project/tasks/task_list';
import ConnectionList from './connection_list';
import LeadsList from './leads/list';
import Leads from './leads/leads';
import { LAST_UPDATED } from 'bundles/leads/index/table/cell';
import LeadImporter from './leads/importer';
import ActivityFeed from './activity_feed';
import { ProjectPropTypes } from 'components/project/prop_types';
import { ActionIndicatorType } from 'bundles/project/prop_types/utils';
import GridsTab from './grids_tab';
import PropertyTab from './property_tabs/property_tab';
import ProjectLayout from 'components/project_layout/project_layout';
import LayoutHeader from 'components/project_layout/layout_header';
import DemographicsTab from 'bundles/demographics';
import {
  fetchConnectableObjects,
  fetchDeals,
  fetchLeads,
  fetchTasks,
  hideActionIndicator,
  selectShadow,
  showActionIndicator,
  showNewNoteModal,
  showNewTaskModal,
  toggleAlert,
  setShadowDirty,
  updateLeadNote
} from '../actions/index';
import { fetchMetrics, fetchFastMetrics, fetchAnalyticsMetrics } from 'components/project/metrics/api';
import {
  addClientReportCard,
  batchUpdateClientReportCards,
  deleteProject,
  removeClientReportCard,
  resendClientReportShareableToken,
  updateAccessLevels,
  updateClientReportCard,
  updateCollaborators,
  updateNotificationsBlockedUserIds,
  updateVaultData
} from 'components/project/actions';
import DuplicationForm from 'components/project/duplication_form';
import EmailModal from './email_modal';
import TaskModal from 'components/project/task_modal';
import CrmActivities from 'bundles/apex/activities';
import CrmProperty from './crm_property';
import CrmTasks from 'bundles/apex/tasks/list';
import DocsTab from 'components/project/docs_tab';
import ReportTab from 'bundles/client_reports/report_tab';
import DealsTab from './deals_tab';
import VouchersTab from './vouchers_tab';
import SharingActivityTab from 'components/project/sharing_activity/sharing_activity_tab';
import CompsTab from './external_connections/comps/comps_tab';
import PeopleTab from './property_contacts/people/people_tab';
import CompaniesTab from './property_contacts/companies/companies_tab';
import MediaTab from './media_tab/media_tab';
import ProjectDealsSidebar from 'bundles/apex/deals/project_sidebar';
// import LeadModal from './leads/apex/modal';
import LeadModal from './leads/modal';
import ConfirmListingIsUpToDateModal from './confirm_listing_is_up_to_date_modal';
import { buildParentOption } from 'bundles/crm/parent_helpers';
import UpdatedClientReport from '../../updated_client_report';
import { isFetchingLeads, shouldFetchLeads } from '../selectors/leads';

export const defaultFilterValue = {
  caStatusFilter: [],
  companyFilter: [],
  listingFilter: [],
  profileUserTypeFilter: [],
  searchText: '',
  statusFilter: [],
  referralSourceFilter: [],
  waitlistFilter: [],
  startDate: null,
  endDate: null
};

export class ProjectNavigator extends React.Component {
  static propTypes = {
    actionIndicators: PropTypes.objectOf(ActionIndicatorType),
    activeTab: PropTypes.string,
    activityCsvVaultsPath: PropTypes.string,
    addClientReportCard: PropTypes.func.isRequired,
    alert: PropTypes.object.isRequired,
    areaAnalyticsTabPath: PropTypes.string,
    availableDatabaseOptions: PropTypes.array.isRequired,
    batchUpdateClientReportCards: PropTypes.func.isRequired,
    caDownloadPath: PropTypes.string,
    callLists: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired
    })),
    canDisplayEmailLink: PropTypes.bool.isRequired,
    canDuplicateProject: PropTypes.bool.isRequired,
    canShowDemographicsPoc: PropTypes.bool,
    clientReportActive: PropTypes.bool.isRequired,
    commercialEdgeLogoPath: PropTypes.string.isRequired,
    companyFilterOptions: PropTypes.array,
    connectableObjects: PropTypes.arrayOf(PropTypes.shape({
      connections: PropTypes.array.isRequired
    })),
    currentUser: PropTypes.shape({
      callListsEnabled: PropTypes.bool
    }).isRequired,
    databaseLinksAccess: PropTypes.bool.isRequired,
    dealFormProps: PropTypes.object,
    dealPaths: PropTypes.object,
    deals: PropTypes.array,
    defaultTaskAssignee: PropTypes.object,
    deleteProject: PropTypes.func.isRequired,
    demographicsOptions: PropTypes.object,
    demographicsTabPath: PropTypes.string,
    disabledTooltips: PropTypes.object,
    docTabPath: PropTypes.string,
    documentCenter: PropTypes.bool,
    edgeCrm: PropTypes.shape({
      activities: PropTypes.shape({
        paths: PropTypes.object.isRequired,
      }).isRequired,
      researchProperty: PropTypes.object,
      tasks: PropTypes.shape({
        paths: PropTypes.object.isRequired,
      }).isRequired,
    }),
    emailCa: PropTypes.bool,
    emailTabPath: PropTypes.string,
    features: PropTypes.shape({
      apex: PropTypes.bool,
      crm: PropTypes.bool.isRequired,
      leadManagement: PropTypes.bool,
      projectAttachments: PropTypes.bool,
      projectTasksActivities: PropTypes.bool,
      projectCollaborators: PropTypes.bool,
      reminders: PropTypes.bool,
      shadowGrid: PropTypes.bool,
    }).isRequired,
    fetchConnectableObjects: PropTypes.func.isRequired,
    fetchDeals: PropTypes.func.isRequired,
    fetchingLeads: PropTypes.bool.isRequired,
    fetchLeads: PropTypes.func.isRequired,
    fetchTasks: PropTypes.func.isRequired,
    forUser: PropTypes.shape({
      id: PropTypes.number.isRequired,
      canDeleteAttachments: PropTypes.bool,
    }),
    helpCenterUrl: PropTypes.string,
    hideActionIndicator: PropTypes.func.isRequired,
    leadFieldFormat: PropTypes.object,
    leadReferralSourceFilterOptions: PropTypes.array,
    leadReferralSources: PropTypes.array,
    leads: PropTypes.array,
    leadsPath: PropTypes.string,
    leadStatuses: PropTypes.array,
    mediaTabPath: PropTypes.string,
    paths: PropTypes.object,
    plansTabPath: PropTypes.string,
    project: ProjectPropTypes.isRequired,
    propertyComps: PropTypes.object,
    removeClientReportCard: PropTypes.func,
    resendClientReportShareableToken: PropTypes.func.isRequired,
    royaltiesTabPath: PropTypes.string,
    selectShadow: PropTypes.func.isRequired,
    setShadowDirty: PropTypes.func.isRequired,
    shadowGridPath: PropTypes.string,
    sharingTabPath: PropTypes.string,
    showActionIndicator: PropTypes.func.isRequired,
    showClientReport: PropTypes.bool,
    showNewNoteModal: PropTypes.func.isRequired,
    showNewTaskModal: PropTypes.func.isRequired,
    showProjectConnections: PropTypes.bool,
    signsTabPath: PropTypes.string,
    syndicationTabPath: PropTypes.string,
    tasksCsvPath: PropTypes.string,
    toggleAlert: PropTypes.func.isRequired,
    updateAccessLevels: PropTypes.func.isRequired,
    updateClientReportCard: PropTypes.func.isRequired,
    updateCollaborators: PropTypes.func.isRequired,
    updateLeadNote: PropTypes.func.isRequired,
    updateNotificationsBlockedUserIds: PropTypes.func.isRequired,
    updateVaultData: PropTypes.func.isRequired,
    users: PropTypes.array,
    vaultActivitiesPropertyPath: PropTypes.string,
    vaultActivitiesSvgPath: PropTypes.string,
    vouchersSearchPath: PropTypes.string,
    websiteTabPath: PropTypes.string
  };

  state = {
    filters: defaultFilterValue,
    leadsList: {
      page: 1,
      sortBy: { field: LAST_UPDATED, reverse: true },
    },
    modalContent: null,
    showCollaboratorSelectionModal: false,
    showDuplicationForm: false,
    showLeadImporter: false
  }

  LISTING_TABS = {
    docs: {
      icon: faFileAlt,
      label: 'Documents',
      path: this.props.docTabPath,
    },
    doc_sharing: {
      icon: faLock,
      label: 'Sharing',
      path: this.props.sharingTabPath
    },
    project_leads: {
      icon: faAddressBook,
      label: 'Leads'
    },
    sharing_activity: {
      icon: faTachometerAlt,
      label: 'Web Activity',
      path: this.props.vaultActivitiesPropertyPath
    },
    website: {
      icon: faGlobe,
      path: this.props.websiteTabPath
    },
    email: {
      icon: faEnvelope,
      path: this.props.emailTabPath
    },
    syndication: {
      icon: faSatelliteDish,
      path: this.props.syndicationTabPath
    },
    grids: {
      icon: faBorderAll,
      label: 'Grids',
      path: this.props.shadowGridPath,
    },
    plans: {
      icon: faObjectGroup,
      path: this.props.plansTabPath
    },
    media: {
      icon: faImage,
      label: 'Media',
      path: this.props.mediaTabPath
    },
    demographics: {
      icon: faBullseye,
      path: this.props.demographicsTabPath
    },
    area_analytics: {
      icon: faSquarePollVertical,
      label: 'Area Analytics',
      path: this.props.areaAnalyticsTabPath
    },
    signs: {
      icon: faRoad,
      path: this.props.signsTabPath
    }
  }

  DEAL_TABS = {
    deals: {
      icon: faFileInvoiceDollar,
      path: this.props.dealPaths,
    },
    vouchers: {
      icon: faFileInvoiceDollar,
      path: this.props.vouchersSearchPath
    },
    'deal-royalties': {
      icon: faDollarSign,
      label: 'Royalties',
      path: this.props.royaltiesTabPath
    }
  }

  CONNECTIONS_TABS = {
    contacts: {
      icon: faUsers,
      label: 'Contacts',
      path: '/contacts'
    },
    companies: {
      icon: faBriefcase,
      label: 'Companies',
      path: '/companies'
    },
    comps: {
      icon: faTags,
      label: 'Comps',
      path: '/comps'
    }
  }

  hideSyndicationIndicator = () => {
    this.props.hideActionIndicator('syndication');
  }
  showSyndicationIndicator = () => {
    this.props.showActionIndicator('syndication');
  }

  componentDidMount() {
    const { apex, projectTasksActivities } = this.props.features;

    if (projectTasksActivities) this.props.fetchTasks();
    if (apex) this.props.fetchDeals(this.props.dealPaths.index);

    // TODO: de-register these listeners in componentWillUnmount
    document.addEventListener('accessLevelsChanged', ({ detail: { accessLevels, vaultId } }) => {
      this.props.updateAccessLevels(accessLevels, vaultId);
    });

    document.addEventListener('updateVaultData', ({ detail }) => {
      this.props.updateVaultData(detail);
    });

    window.addEventListener('sendRepEmailModal:send', this.hideSyndicationIndicator);
    window.addEventListener('sendRepEmailModal:pending', this.showSyndicationIndicator);
  }

  componentWillUnmount() {
    window.removeEventListener('sendRepEmailModal:send', this.hideSyndicationIndicator);
    window.removeEventListener('sendRepEmailModal:pending', this.showSyndicationIndicator);
  }

  fetchProjectMetrics = startDate => fetchMetrics(this.props.project.id, startDate);

  fetchProjectAnalyticsMetrics = (startDate, endDate = null) => fetchAnalyticsMetrics(this.props.project.id, startDate, endDate);

  fetchFastProjectMetrics = (startDate, endDate = null) => fetchFastMetrics(this.props.project.id, startDate, endDate);

  baseUrl = () => `/projects/${this.props.project.id}`;

  projectIsPersisted() {
    return !!this.props.project.id;
  }

  showClientReport() {
    return this.props.showClientReport && this.projectIsPersisted();
  }

  showOverviewTab() {
    return this.showTaskActivityTabs();
  }

  showLeadsTab() {
    return this.projectIsPersisted() && this.props.features.leadManagement;
  }

  showTaskActivityTabs() {
    return this.props.features.projectTasksActivities && this.projectIsPersisted();
  }

  isEdgeCrmEnabled() {
    return !!this.props.edgeCrm;
  }

  propertyButtonsVisible() {
    return this.projectIsPersisted() && !!this.props.project.editLink && !this.props.project.hidePropertyButtons;
  }

  projectSubtitle = () => {
    const { name, researchProperty: researchProperty } = this.props.project;

    if (name !== researchProperty.address) {
      return researchProperty.addressTwoLine.join(', ');
    } else {
      return researchProperty.addressTwoLine[1];
    }
  }

  numUnconnectedObjectsLabel = () => {
    const count = filter(this.props.connectableObjects || [], ({ connections }) => connections.length === 0).length;

    return count === 0 ? '' : count.toString();
  }

  deleteProject = () => {
    if (this.projectIsPersisted()) {
      const message = 'Deleting a project is permanent. ' +
        'You won\'t be able to access any of its information, photos, or documents.';
      confirm(message, {
        buttonLabel: 'Delete Project',
        buttonStyle: 'danger',
        title: `Delete ${this.props.project.name}?`
      }).then(() => {
        this.props.deleteProject(this.props.project.id, (redirectPath) => {
          window.location.assign(redirectPath);
        });
      }).catch(() => { });
    }
  }

  showDuplicationForm = () => {
    this.setState({ showDuplicationForm: true });
  }

  hideDuplicationForm = () => {
    this.setState({ showDuplicationForm: false });
  }

  showLeadImporter = () => {
    this.setState({ showLeadImporter: true });
  }

  hideLeadImporter = (importJobId) => {
    if (importJobId) {
      const { fetchConnectableObjects, fetchLeads, project } = this.props;

      fetchLeads(project.id).then(() => this.setState({ showLeadImporter: false }));
      fetchConnectableObjects(project.id);
    } else {
      this.setState({ showLeadImporter: false });
    }
  }

  editProject = (e) => {
    if (this.projectIsPersisted()) {
      clickAsLink(window, this.props.project.editLink, e);
    }
  }

  updateCollaborators = (selectedUsers) => {
    return this.props.updateCollaborators(this.props.project.id, selectedUsers);
  }

  handleFilterChange = (filter) => {
    this.setState(prevState => (
      { filters: { ...prevState.filters, ...filter } }
    ));
  }

  setPage = (page) => {
    this.setState(prevState => (
      { leadsList: { ...prevState.leadsList, page } }
    ));
  }

  changeSort = (fieldClicked) => {
    this.setState((prevState) => {
      const prevSortBy = prevState.leadsList.sortBy;
      const reverse = prevSortBy.field === fieldClicked ? !prevSortBy.reverse : false;

      return { leadsList: { ...prevState.leadsList, sortBy: { field: fieldClicked, reverse } } };
    });
  }

  hasMarketingTabs() {
    return !!window.tabSupport;
  }

  showConnectionsTab = () => {
    return this.projectIsPersisted() && this.props.showProjectConnections;
  };

  isPropertyCompsEnabled = () => this.props.propertyComps && this.props.propertyComps.enabled;
  showDealsTab = () => find(this.DEAL_TABS, tab => tab.path);

  handleDismissAlert = () => {
    this.props.toggleAlert('', false);
  }

  isValidTab = (availableTabs, activeTab) => Object.values(availableTabs)
    .reduce((cont, { subtabs }) => [...cont, ...Object.keys(subtabs || {})], [])
    .includes(activeTab)

  activeTab = () => this.isValidTab(this.availableTabs(), this.props.activeTab) ? this.props.activeTab : null

  renderWorkspaceTabs() {
    const workspaceRoutes = [];

    workspaceRoutes.push(
      <Route key="attachments" path="/attachments">
        <DocsTab />
      </Route>
    );

    if (this.showLeadsTab()) {
      workspaceRoutes.push(this.leadsRoute());
    }

    if (this.showTaskActivityTabs()) {
      workspaceRoutes.push(
        <Route key="tasks" path="/project_tasks">
          <TaskList
            defaultTaskAssignee={this.props.defaultTaskAssignee}
            multipleProjectsInTask={true}
            tasksCsvPath={this.props.tasksCsvPath}
          />
        </Route>
      );
      workspaceRoutes.push(
        <Route key="activities" path="/activities">
          <ActivityFeed />
        </Route>
      );
    }

    if (this.showClientReport()) {
      if (this.props.project.updatedClientReport) {
        workspaceRoutes.push(
          <Route key="client-report" path="/client_report">
            <UpdatedClientReport
              collaborators={this.props.project.collaborators}
              defaultCards={this.props.project.clientReportV2Cards}
              fetchAnalyticsMetrics={this.fetchProjectAnalyticsMetrics}
              fetchFastMetrics={this.fetchFastProjectMetrics}
              initialEndDate={this.props.project.clientReportEndDate}
              initialStartDate={this.props.project.clientReportStartDate}
              project={this.props.project}
            />
          </Route>
        );
      } else {
        workspaceRoutes.push(
          <Route key="client-report" path="/client_report">
            <ReportTab
              addCard={this.props.addClientReportCard}
              batchUpdateCards={this.props.batchUpdateClientReportCards}
              cards={this.props.project.clientReportCards}
              fetchAnalyticsMetrics={this.fetchProjectAnalyticsMetrics}
              fetchFastMetrics={this.fetchFastProjectMetrics}
              fetchingLeads={this.props.fetchingLeads}
              fetchLeads={this.props.fetchLeads}
              fetchMetrics={this.fetchProjectMetrics}
              hideLastActivity={!this.props.features.projectTasksActivities}
              leads={this.props.leads}
              leadStatuses={this.props.leadStatuses}
              project={this.props.project}
              projectId={this.props.project.id}
              removeCard={this.props.removeClientReportCard}
              resendShareableToken={this.props.resendClientReportShareableToken}
              updateCard={this.props.updateClientReportCard}
              updateLeadNote={this.props.updateLeadNote}
            />
          </Route>
        );
      }
    }

    if (this.props.databaseLinksAccess) {
      workspaceRoutes.push(
        <Route key="db-links" path="/database_links">
          <ConnectionList />
        </Route>
      );
    }

    return workspaceRoutes;
  }

  leadsRoute() {
    if (this.state.showLeadImporter) {
      return (
        <LeadImporter
          crmEnabled={this.props.features.crm}
          fieldFormat={this.props.leadFieldFormat}
          importPath={this.props.leadsPath}
          onFinishImport={this.hideLeadImporter}
        />
      );
    } else {
      const { currentUser } = this.props;

      return (
        <Route key="leads" path="/project_leads">
          <Leads
            documentCenter={this.props.documentCenter}
            tasksEnabled={this.showTaskActivityTabs()}
          >
            <LeadsList
              availableDatabaseOptions={this.props.availableDatabaseOptions}
              caDownloadPath={this.props.caDownloadPath}
              callLists={this.props.callLists}
              callListsEnabled={currentUser && currentUser.callListsEnabled}
              canDisplayEmailLink={this.props.canDisplayEmailLink}
              changeSort={this.changeSort}
              clientReportActive={this.props.clientReportActive}
              commercialEdgeLogoPath={this.props.commercialEdgeLogoPath}
              databaseMembers={[...this.props.project.collaborators, ...this.props.project.otherAuthorizedUsers]}
              defaultTaskAssignee={this.props.defaultTaskAssignee}
              documentCenter={this.props.documentCenter}
              emailCa={this.props.emailCa}
              filters={this.state.filters}
              handleFilterChange={this.handleFilterChange}
              hasTasksEnabled={this.showTaskActivityTabs()}
              leadReferralSources={this.props.leadReferralSourceFilterOptions}
              leadsPath={this.props.leadsPath}
              leadStatuses={this.props.leadStatuses}
              page={this.state.leadsList.page}
              setPage={this.setPage}
              sort={this.state.leadsList.sortBy}
              useVaultGates={this.props.project.useVaultGates}
              onStartImport={this.showLeadImporter}
            />
          </Leads>
        </Route>
      );
    }
  }

  renderMarketingTabContent() {
    if (!this.hasMarketingTabs()) return;

    const marketingRoutes = [];

    if (this.props.docTabPath) {
      marketingRoutes.push(
        <Route key="docs" path="/docs">
          <PropertyTab
            javascriptInitializer={window.tabSupport.docTab}
            shouldRenderPanel={!this.props.documentCenter}
            tabPath={this.props.docTabPath}
          />
        </Route>
      );
    }

    if (this.props.features.shadowGrid) {
      marketingRoutes.push(
        <Route key="grids" path="/grids">
          <GridsTab />
        </Route>
      );
    }

    if (this.props.sharingTabPath) {
      marketingRoutes.push(
        <Route key="doc-sharing" path="/doc_sharing">
          <PropertyTab
            className="doc-sharing"
            javascriptInitializer={window.tabSupport.sharingTab}
            tabPath={this.props.sharingTabPath}
          />
        </Route>
      );
    }

    if (this.props.websiteTabPath) {
      marketingRoutes.push(
        <Route key="website" path="/website">
          <PropertyTab javascriptInitializer={window.tabSupport.websiteTab} tabPath={this.props.websiteTabPath} />
        </Route>
      );
    }

    if (this.props.emailTabPath) {
      marketingRoutes.push(
        <Route key="email" path="/email">
          <PropertyTab javascriptInitializer={window.tabSupport.emailTab} tabPath={this.props.emailTabPath} />
        </Route>
      );
    }

    if (this.props.syndicationTabPath) {
      marketingRoutes.push(
        <Route key="syndication" path="/syndication">
          <PropertyTab
            javascriptInitializer={window.tabSupport.syndicationTab}
            shouldRenderPanel={false}
            tabPath={this.props.syndicationTabPath}
          />
        </Route>
      );
    }

    if (this.props.plansTabPath) {
      marketingRoutes.push(
        <Route key="plans" path="/plans">
          <PropertyTab javascriptInitializer={window.tabSupport.plansTab} tabPath={this.props.plansTabPath} />
        </Route>
      );
    }

    if (this.props.mediaTabPath) {
      marketingRoutes.push(
        <Route key="media" path="/media">
          <MediaTab syndication={this.props.syndicationTabPath ? true : false} />
        </Route>
      );
    }

    if (this.props.demographicsTabPath) {
      if (this.props.canShowDemographicsPoc) {
        marketingRoutes.push(
          <Route key="demographics" path="/demographics">
            <DemographicsTab
              {...this.props.demographicsOptions}
            />
          </Route>
        );
      } else {
        marketingRoutes.push(
          <Route key="demographics" path="/demographics">
            <PropertyTab
              javascriptInitializer={window.tabSupport.demographicsTab}
              tabPath={this.props.demographicsTabPath}
            />
          </Route>
        );
      }
    }

    if (this.props.areaAnalyticsTabPath) {
      marketingRoutes.push(
        <Route key="area_analytics" path="/area_analytics">
          <PropertyTab
            tabPath={this.props.areaAnalyticsTabPath}
          />
        </Route>
      );
    }

    if (this.props.signsTabPath) {
      marketingRoutes.push(
        <Route key="signs" path="/signs">
          <PropertyTab tabPath={this.props.signsTabPath} />
        </Route>
      );
    }

    if (this.props.vaultActivitiesPropertyPath && this.showLeadsTab()) {
      marketingRoutes.push(
        <Route key="sharing_activity" path="/sharing_activity">
          <SharingActivityTab
            activityCsvVaultsPath={this.props.activityCsvVaultsPath}
            vaultActivitiesPropertyPath={this.props.vaultActivitiesPropertyPath}
            vaultActivitiesSvgPath={this.props.vaultActivitiesSvgPath}
          />
        </Route>
      );
    }

    return marketingRoutes;
  }

  renderDealTabContent() {
    if (!this.showDealsTab()) return;

    const dealRoutes = [];

    if (this.props.royaltiesTabPath) {
      dealRoutes.push(
        <Route key="deal-royalties" path="/deal-royalties">
          <PropertyTab tabPath={this.props.royaltiesTabPath} />
        </Route>
      );
    }

    if (this.props.dealPaths) {
      dealRoutes.push(
        <Route key="deals" path="/deals">
          <DealsTab
            features={this.props.features}
            formProps={this.props.dealFormProps}
            paths={this.props.dealPaths}
          />
        </Route>
      );
    }

    if (this.props.vouchersSearchPath) {
      dealRoutes.push(
        <Route key="vouchers" path="/vouchers">
          <VouchersTab
            proposal={this.props.project.proposal}
            vouchersSearchPath={this.props.vouchersSearchPath}
          />
        </Route>
      );
    }

    return dealRoutes;
  }

  renderConnectionsTabContent() {
    if (!this.showConnectionsTab()) return;

    const connectionRoutes = [];

    if (this.hasMarketingTabs()) {
      connectionRoutes.push(
        <Route key="contacts" path="/contacts">
          <PeopleTab />
        </Route>
      );

      connectionRoutes.push(
        <Route key="companies" path="/companies">
          <CompaniesTab />
        </Route>
      );
    }

    if (this.isPropertyCompsEnabled()) {
      connectionRoutes.push(
        <Route key="comps" path="/comps">
          <CompsTab />
        </Route>
      );
    }
    return connectionRoutes;
  }

  renderEdgeCrmFeatures() {
    const routes = [];
    const staticChildren = [];

    if (this.isEdgeCrmEnabled()) {
      const { edgeCrm, forUser, project } = this.props;

      const parentOptions = [];
      const addParentOption = (record, label, labelSuffix) => {
        const parentType = record === project ? 'Project' : 'Listing';
        parentOptions.push(buildParentOption(record, parentType, label, labelSuffix));
      };

      addParentOption(project, 'None');

      if (project.saleChecked) {
        addParentOption(project.listing, 'Sale');

        for (const lotListing of project.lotListings) {
          addParentOption(lotListing, 'Lot', lotListing.address2);
        }

        for (const condoListing of project.condoListings) {
          addParentOption(condoListing, 'Condo', condoListing.address2);
        }
      }

      if (project.leaseChecked) {
        for (const leaseSpaceListing of project.leaseSpaceListings) {
          addParentOption(leaseSpaceListing, 'Lease Space', leaseSpaceListing.address2);
        }
      }

      const currentParent = { id: project.id, type: 'Project' };

      routes.push(
        <Route key="crm-tasks" path="/crm_tasks">
          <CrmTasks
            currentParent={currentParent}
            filters={{
              dateRange: { enabled: false },
              dateType: { enabled: false },
              dealUser: { enabled: false },
              office: { enabled: false },
              oneLine: true,
              options: { users: project.collaborators },
              parent: { enabled: false },
              status: { enabled: false },
            }}
            forUser={edgeCrm.forUser}
            options={{ parents: parentOptions }}
            paths={edgeCrm.tasks.paths}
            showParents
            showRouterPrompt
          />
        </Route>
      );

      routes.push(
        <Route key="crm-activities" path="/crm_activities">
          <CrmActivities
            currentParent={currentParent}
            header="Activities"
            inboxAddress={project.inboxEmailAddress}
            options={{
              parents: parentOptions,
            }}
            paths={edgeCrm.activities.paths}
            showParents
            tagText='Use "@" to tag a user'
          />
        </Route>
      );
    }

    return { routes, staticChildren };
  }

  renderTabContent() {
    const { routes: edgeCrmRoutes, staticChildren: edgeCrmStaticChildren } = this.renderEdgeCrmFeatures();

    return (
      <div className="container-fluid project-tab-container px-5 h-100">
        <Switch>
          <Route path="/overview">
            <ProjectOverview
              handleFilterChange={this.handleFilterChange}
              helpCenterUrl={this.props.helpCenterUrl}
              leadStatuses={this.props.leadStatuses}
              showNewNoteModal={this.props.showNewNoteModal}
              showNewTaskModal={this.props.showNewTaskModal}
            />
          </Route>
          {this.renderWorkspaceTabs()}
          {this.renderMarketingTabContent()}
          {this.renderDealTabContent()}
          {this.renderConnectionsTabContent()}
          {edgeCrmRoutes}
        </Switch>

        {edgeCrmStaticChildren}
      </div>
    );
  }

  availableTabs() {
    const tabs = {};

    tabs.project = {
      icon: faTh,
      subtabs: {}
    };

    if (this.showOverviewTab()) {
      tabs.project.subtabs.overview = { icon: faHome };
    }

    if (this.showTaskActivityTabs()) {
      tabs.project.subtabs.project_tasks = { icon: faCheck, label: 'Tasks' };
      tabs.project.subtabs.activities = { icon: faBolt, label: 'Activity Feed' };
    }

    if (this.showLeadsTab()) {
      tabs.project.subtabs.project_leads = { icon: faAddressBook, label: 'Leads' };
    }

    if (this.showClientReport()) {
      tabs.project.subtabs.client_report = { icon: faChartBar, label: 'Client Report' };
    }

    if (this.props.features.projectAttachments) {
      tabs.project.subtabs.attachments = { icon: faPaperclip };
    }

    if (this.isEdgeCrmEnabled()) {
      tabs.project.subtabs.crm_tasks = { icon: faCheck, label: 'Tasks' };
      tabs.project.subtabs.crm_activities = { icon: faBolt, label: 'Activities' };
    }

    if (this.props.databaseLinksAccess) {
      tabs.project.subtabs.database_links = {
        badge: this.numUnconnectedObjectsLabel(),
        icon: faLink,
        label: 'Database Links'
      };
    }

    // If leads is the only tab available in the project subtabs, then remove it so it can be displayed
    // in the listings tab instead
    if (size(tabs.project.subtabs) === 1) delete tabs.project.subtabs.project_leads;

    if (this.hasMarketingTabs()) {
      tabs.listing = {
        icon: faBullhorn,
        subtabs: {}
      };

      const listingTabs = { ...this.LISTING_TABS };

      // Remove any lead related tabs from listings if we're not supposed to show them
      if (!this.showLeadsTab()) {
        delete listingTabs.sharing_activity;
        delete listingTabs.project_leads;
      }

      // If leads are already being shown in the project section, remove them from the listing section
      if (tabs.project.subtabs.project_leads) delete listingTabs.project_leads;

      // undo the key camelcasing so that the keys match listingTabs
      const disabledTooltips = snakecaseKeys(this.props.disabledTooltips || {});

      each(listingTabs, (tabData, key) => {
        if (!tabData.hasOwnProperty('path') || tabData.path) {
          tabs.listing.subtabs[key] = { ...tabData };

          if (disabledTooltips[key]) {
            tabs.listing.subtabs[key].disabledTooltip = disabledTooltips[key];
          }

          // we only add the indicators for listingTabs for now
          if (this.props.actionIndicators && this.props.actionIndicators[key]) {
            tabs.listing.subtabs[key].actionIndicator = this.props.actionIndicators[key];
          }
        }
      });
    }

    if (this.showDealsTab()) {
      tabs.deal = {
        icon: faHandshake,
        subtabs: {}
      };

      each(this.DEAL_TABS, (tabData, key) => {
        if (tabData.path) {
          tabs.deal.subtabs[key] = tabData;
        }
      });
    }

    if (this.showConnectionsTab()) {
      tabs.connections = {
        icon: null,
        subtabs: {}
      };

      each(this.CONNECTIONS_TABS, (tabData, key) => {
        if (tabData.path) {
          tabs.connections.subtabs[key] = tabData;
        }
      });

      if (!this.hasMarketingTabs()) {
        delete tabs.connections.subtabs.contacts;
        delete tabs.connections.subtabs.companies;
      }

      if (!this.isPropertyCompsEnabled()) delete tabs.connections.subtabs.comps;
    }

    return tabs;
  }

  renderCTAButtons() {
    let propertyButtons;

    if (this.propertyButtonsVisible()) {
      propertyButtons = (
        <div className="text-right">
          <button
            className="btn btn-dark btn-sm"
            disabled={!this.projectIsPersisted()}
            id="edit-listing-btn"
            onClick={this.editProject}
          >
            Edit Listing
          </button>
          {!this.props.project.userIsOutsideBroker &&
            <Dropdown disabled={!this.projectIsPersisted()} id="project-action-dropdown">
              <UnstyledDropdownToggle
                bsRole="toggle"
                id="project-action-button"
                variant="dark"
              >
                <button className="btn btn-dark btn-sm left-buffer-md" disabled={!this.projectIsPersisted()}>
                  <FontAwesomeIcon icon={faCogs} size="lg" />
                </button>
              </UnstyledDropdownToggle>
              <Dropdown.Menu className="dropdown-menu-right">
                <MenuItem eventKey={1} id="delete-listing-btn" onClick={this.deleteProject}>
                  <strong>Delete Listing</strong>
                </MenuItem>
                {this.props.canDuplicateProject &&
                  <MenuItem eventKey={2} id="duplicate-listing-btn" onClick={this.showDuplicationForm}>
                    <strong>Duplicate Listing</strong>
                  </MenuItem>}
              </Dropdown.Menu>
            </Dropdown>
          }
        </div>
      );
    }

    return (
      <div className="row-eq-height align-top">
        {propertyButtons}
      </div>
    );
  }

  renderAlert() {
    const { error, message } = this.props.alert;
    if (!message) return;

    return (
      <Alert bsStyle={error ? 'danger' : 'success'} className="m-0" onDismiss={this.handleDismissAlert}>
        <div className="container-fluid">
          <span dangerouslySetInnerHTML={{ __html: message }} />
        </div>
      </Alert>
    );
  }

  renderAssociationLinks() {
    if (this.isEdgeCrmEnabled()) {
      return (
        <div className="mt-2">
          <CrmProperty/>
        </div>
      );
    }
  }

  render() {
    const canEditCollaborators = this.propertyButtonsVisible();
    const setDirty = this.props.setShadowDirty;
    const selectShadow = this.props.selectShadow;
    return (
      <Router
        basename={this.baseUrl()}
        getUserConfirmation={(message, callback) => DirtyShadowConfirmationPrompt(
          message,
          callback,
          selectShadow,
          setDirty
        )}
      >
        <div className="project-wrapper">
          {this.renderAlert()}
          <LayoutHeader
            collaborators={this.props.project.collaborators}
            hasDealStatusLabelDisabled={this.props.project.clientCustomizations.hasDealStatusLabelDisabled}
            status={this.props.project.status}
            subtitle={this.projectSubtitle()}
            tertiaryTitle={this.renderAssociationLinks()}
            thumbnailUrl={this.props.project.thumbnailUrl}
            title={this.props.project.name}
            useUpdatedStyles={true}
            onCollaboratorsUpdated={canEditCollaborators ? this.updateCollaborators : null}
            onUpdateNotificationsBlockedUserIds={this.props.updateNotificationsBlockedUserIds}
          >
            {this.renderCTAButtons()}
          </LayoutHeader>
          <ProjectLayout
            activeTab={this.activeTab()}
            tabs={this.availableTabs()}
          >
            {this.renderTabContent()}
          </ProjectLayout>
          {this.projectIsPersisted() && (
            <React.Fragment>
              {this.propertyButtonsVisible() && (
                <DuplicationForm
                  projectId={this.props.project.id}
                  projectTitle={this.props.project.name}
                  show={this.state.showDuplicationForm}
                  onHide={this.hideDuplicationForm}
                />
              )}
              <EmailModal projectTasksActivities={this.props.features.projectTasksActivities} />
              {this.showOverviewTab() && (
                <React.Fragment>
                  <TaskModal defaultTaskAssignee={this.props.defaultTaskAssignee} />
                </React.Fragment>
              )}
            </React.Fragment>
          )}
          {this.showLeadsTab() && (
            <LeadModal
              leadReferralSources={this.props.leadReferralSources}
              leadStatuses={this.props.leadStatuses}
            />
          )}

          <ConfirmListingIsUpToDateModal
            channels={this.props.project.syndicationChannelNames}
            daysWithoutUpdate={this.props.project.daysWithoutUpdate}
            needsConfirmation={this.props.project.needsUpToDateConfirmationForSyndication}
            projectDisplayName={this.props.project.displayName}
            syndicationTouchPath={this.props.project.syndicationTouchPath}
            onEditProperty={this.editProject}
          />

          {this.props.features.apex && (
            <ProjectDealsSidebar
              deals={this.props.deals}
              features={this.props.features}
              forUser={this.props.forUser}
              options={{ users: this.props.users }}
              paths={this.props.paths}
            />
          )}
        </div>
      </Router>
    );
  }
}

const mapStateToProps = state => ({
  actionIndicators: state.actionIndicators,
  activeTab: state.ui.activeTab,
  alert: state.alert,
  connectableObjects: state.connectableObjects,
  currentUser: state.currentUser,
  deals: state.deals,
  features: state.features,
  fetchingLeads: !!isFetchingLeads(state),
  leads: isFetchingLeads(state) || shouldFetchLeads(state) ? [] : state.leads,
  project: state.project,
});

const mapDispatchToProps = {
  addClientReportCard,
  batchUpdateClientReportCards,
  deleteProject,
  fetchConnectableObjects,
  fetchDeals,
  fetchLeads,
  fetchTasks,
  hideActionIndicator,
  removeClientReportCard,
  resendClientReportShareableToken,
  selectShadow,
  showActionIndicator,
  showNewNoteModal,
  showNewTaskModal,
  toggleAlert,
  setShadowDirty,
  updateAccessLevels,
  updateClientReportCard,
  updateCollaborators,
  updateLeadNote,
  updateNotificationsBlockedUserIds,
  updateVaultData
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectNavigator);
